import cogoToast from 'cogo-toast';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { request } from '../../../../../../../../common/src/legacy/services/httpSessionService';
import { StatusResponse } from '../../../../../../../../../types/API';
import { useFlowsRouteParams } from '../../../../../../hooks/useFlowsRouteParams';
import { AskLaterQuestion, AskLaterQuestionList } from '../types';
import { getUseAskLaterQuestionQKey } from './getAskLaterQuestion';
import { useConversation } from '../../../../../../../../conversations/src/features/detail/shared/api/getConversationById';
import usePartyId from '../../../../../../../../common/src/hooks/query-params/usePartyId';

interface PostAskLaterQuestionsPayload {
  party_id: string;
  flow_id: string;
  questions: AskLaterQuestion[];
}

export const postAskLaterQuestions = async (
  payload: PostAskLaterQuestionsPayload,
): Promise<[StatusResponse, number]> => {
  const path = `v1/api/flows-ask-later-questions`;
  const [resp, status] = await request({ path, method: 'POST', payload });
  return [resp, status];
};

export const useAskLaterQuestionsMutation = (): UseMutationResult<
  StatusResponse,
  unknown,
  AskLaterQuestion[],
  AskLaterQuestionList
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { sessionId, flowId } = useFlowsRouteParams();
  const { data } = useConversation(sessionId, { refetchOnMount: false });

  const partyIdParam = usePartyId();
  const partyId = partyIdParam || data?.prospect?.id;
  const queryKey = getUseAskLaterQuestionQKey({
    party_id: partyId || '',
    flow_id: flowId || '',
  });

  return useMutation<
    StatusResponse,
    unknown,
    AskLaterQuestion[],
    AskLaterQuestionList
  >(
    async (questions) => {
      if (!partyId || !flowId) throw new Error();

      const [resp, status] = await postAskLaterQuestions({
        party_id: partyId,
        flow_id: flowId,
        questions,
      });

      if (status >= 400) throw new Error();

      return resp;
    },
    {
      onMutate: async (questions) => {
        await queryClient.cancelQueries(queryKey);

        const prevAskLaterQuestions =
          queryClient.getQueryData<AskLaterQuestionList>(queryKey);

        queryClient.setQueryData(queryKey, {
          ...prevAskLaterQuestions,
          questions: uniqBy(
            [...questions, ...(prevAskLaterQuestions?.questions || [])],
            'question_id',
          ).filter(({ active }) => active),
        });

        return prevAskLaterQuestions;
      },
      onError: (_, __, context) => {
        cogoToast.error(t('COMPANY-SCAN_ASK-LATER_CHECKBOX_CHANGE-FAIL'));
        if (context) queryClient.setQueryData(queryKey, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );
};
